import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRangeController } from "../../profile/periodization/parts/Header";

import moment from "moment";
import { MonthColumn } from "../../profile/periodization/parts/MonthColumn";
import {
  getModifiers,
  getSortedWeekDays,
} from "../../../../utils/annual-planner-helpers";
import { useHorizontalScroll } from "../../../../hooks/useHorizontalScroll";

const from = moment.utc().subtract(3, "months").toDate();
const to = moment.utc().add(9, "months");

const NutritionPlanCalendar = ({
  startingCalendarDay,
  nutritionPlans,
  stats,
  units,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [dateFromState, setDateFromState] = useState(from);
  const [dateToState, setDateToState] = useState(to);
  const weekDays = getSortedWeekDays(startingCalendarDay, t);
  const horizontalScrollRef = useHorizontalScroll("faster");

  const unitLabel = units === "metric" ? "Kg" : "Lbs";

  const nutritionTargetsHeaders = [
    t("nutritionPage.planCalendar.proteinG"),
    t("nutritionPage.planCalendar.carbsG"),
    t("nutritionPage.planCalendar.fatG"),
    t("nutritionPage.planCalendar.fiberG"),
    t("nutritionPage.planCalendar.kcal"),
  ];

  const athleteStatsHeaders = [
    t("nutritionPage.planCalendar.avgBodyweight", { unit: unitLabel }),
    t("nutritionPage.planCalendar.bodyweightChange", { unit: unitLabel }),
    t("nutritionPage.planCalendar.avgDailySteps"),
    t("nutritionPage.planCalendar.avgDailySleep"),
  ];

  const toSort = nutritionPlans.map((x) => x);

  const orderedPlans = toSort.sort((a, b) => a.utcStartDate - b.utcStartDate);

  const selectedDays = orderedPlans.map((plan, i) => ({
    fromDate: moment(String(plan.utcStartDate)).toDate(),
    toDate: moment(String(plan.utcEndDate)).toDate(),
    eventColor: "#02AFE4",
    name: `nutrition-${i}`,
  }));

  const modifiersPleaseWork = getModifiers(selectedDays);

  return (
    <div className="nutrition-plan-calendar">
      <DateRangeController
        dateFromState={dateFromState}
        dateToState={dateToState}
        setDateFromState={setDateFromState}
        setDateToState={setDateToState}
        keepRange={true}
      />

      <section role="planner-wrapper">
        <div role="planner-headers">
          <div className="month-row-header">
            {weekDays.map((day) => (
              <span className="row-header" key={`week-day-${day.index}`}>
                {day.day}
              </span>
            ))}
          </div>

          <div className="athlete-stats-header">
            <span className="row-header-label">
              {t("nutritionPage.planCalendar.trainingDayTargets")}
            </span>
            {nutritionTargetsHeaders.map((stat, index) => (
              <span className="row-header" key={`${stat}-${index}`}>
                {stat}
              </span>
            ))}
          </div>

          <div className="athlete-stats-header">
            <span className="row-header-label">
              {t("nutritionPage.planCalendar.restDayTargets")}
            </span>
            {nutritionTargetsHeaders.map((stat, index) => (
              <span className="row-header" key={`${stat}-${index}`}>
                {stat}
              </span>
            ))}
          </div>

          <div className="athlete-stats-header">
            <span className="row-header-label">
              {t("nutritionPage.planCalendar.bodyStats")}
            </span>
            {athleteStatsHeaders.map((stat, index) => (
              <span className="row-header" key={`${stat}-${index}`}>
                {stat}
              </span>
            ))}
          </div>
        </div>
        <div role="planner-body" ref={horizontalScrollRef}>
          {Array.apply(null, { length: 12 }).map((e, i) => {
            const monthDate = moment(dateFromState).add(i, "months");
            return (
              <MonthColumn
                key={`month-column-${i}`}
                month={monthDate}
                startingCalendarDay={startingCalendarDay}
                selectedDaysState={undefined}
                setSelectedDaysState={undefined}
                modifiers={modifiersPleaseWork.modifiers}
                modifiersStyles={modifiersPleaseWork.modifiersStyles}
                modifiersClassNames={modifiersPleaseWork.modifiersClassNames}
                athleteStatsState={stats}
                isAnnualPlanner={false}
                nutritionPlans={orderedPlans}
              />
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default NutritionPlanCalendar;
